import React, { useRef, useState } from "react";
import moment from "moment";
import logo from "../../assets/images/logo.png";

const BRAND  = "#002B5B";
const BRAND_LIGHT = "#e8eef6";
const BRAND_BORDER = "#b8cce0";

/* ── Print styles injected into popup window ── */
const PRINT_STYLES = `
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: Arial, sans-serif; background: #fff; color: #222; }
  .receipt-wrapper {
    width: 700px; margin: 30px auto; padding: 40px;
    border: 2px solid #002B5B; border-radius: 12px;
  }
  .receipt-header {
    display: flex; justify-content: space-between; align-items: center;
    border-bottom: 2px solid #002B5B; padding-bottom: 20px; margin-bottom: 24px;
  }
  .receipt-header img { height: 60px; }
  .receipt-header-right { text-align: right; }
  .receipt-header-right h2 { color: #002B5B; font-size: 22px; margin-bottom: 4px; }
  .receipt-header-right p { font-size: 12px; color: #666; margin-bottom: 2px; }
  .receipt-title {
    text-align: center; font-size: 20px; font-weight: bold;
    letter-spacing: 2px; color: #002B5B; margin-bottom: 24px; text-transform: uppercase;
  }
  .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px 24px; margin-bottom: 24px; }
  .info-box {
    background: #e8eef6; border: 1px solid #b8cce0; border-radius: 8px; padding: 14px 20px;
  }
  .info-box label { font-size: 11px; color: #888; text-transform: uppercase; letter-spacing: 1px; display: block; margin-bottom: 4px; }
  .info-box span { font-size: 16px; font-weight: 700; color: #002B5B; }
  .section-title { font-size: 13px; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; color: #002B5B; margin-bottom: 10px; }
  .bill-table { width: 100%; border-collapse: collapse; margin-bottom: 24px; }
  .bill-table th { background: #002B5B; color: white; padding: 10px 16px; font-size: 13px; }
  .bill-table th:first-child { text-align: left; }
  .bill-table th:last-child { text-align: right; }
  .bill-table td { padding: 11px 16px; border-bottom: 1px solid #e5e7eb; font-size: 14px; }
  .bill-table td:last-child { text-align: right; font-weight: 600; }
  .bill-table tr:last-child td { border-bottom: none; }
  .bill-table tr:nth-child(even) td { background: #f5f8fc; }
  .footer-note {
    background: #e8eef6; border: 1px solid #b8cce0; border-radius: 8px;
    padding: 12px 16px; margin-bottom: 24px; text-align: center;
    font-size: 13px; color: #002B5B; font-style: italic;
  }
  .receipt-footer {
    text-align: center; font-size: 11px; color: #aaa;
    border-top: 1px solid #e5e7eb; padding-top: 16px; margin-top: 8px;
  }
  @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
`;

export const Bill = () => {
  const [orgName,   setOrgName]   = useState("");
  const [billTitle, setBillTitle] = useState("INVOICE");
  const [billNo,    setBillNo]    = useState("");
  const [billDate,  setBillDate]  = useState(moment().format("YYYY-MM-DD"));
  const [fields,    setFields]    = useState([{ name: "", value: "" }]);
  const [footer,    setFooter]    = useState("");
  const [preview,   setPreview]   = useState(false);
  const receiptRef = useRef();

  const addField    = () => setFields([...fields, { name: "", value: "" }]);
  const removeField = (i) => setFields(fields.filter((_, idx) => idx !== i));
  const updateField = (i, key, val) => {
    const updated = [...fields];
    updated[i] = { ...updated[i], [key]: val };
    setFields(updated);
  };

  const handleGenerate = () => {
    if (!orgName.trim()) { alert("Please enter organization name"); return; }
    setPreview(true);
  };

  const handlePrint = () => {
    const w = window.open("", "_blank", "width=800,height=700");
    w.document.write(
      `<!DOCTYPE html><html><head><title>${billTitle} ${billNo ? "#" + billNo : ""}</title><style>${PRINT_STYLES}</style></head><body>${receiptRef.current.innerHTML}</body></html>`
    );
    w.document.close();
    w.focus();
    setTimeout(() => { w.print(); w.close(); }, 300);
  };

  const filledFields = fields.filter(f => f.name.trim());

  /* ── FORM ── */
  if (!preview) return (
    <div className="DashboardMain">
      <div style={{ maxWidth: 660 }}>
        <h3 style={{ marginBottom: 24 }}>Generate Bill</h3>

        <div className="form-group mb-3">
          <label style={{ fontWeight: 600 }}>
            Organization / Issued To <span style={{ color: "#ef4444" }}>*</span>
          </label>
          <input type="text" className="form-control" value={orgName}
            onChange={e => setOrgName(e.target.value)}
            placeholder="e.g. Sohail University" />
        </div>

        <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
          <div className="form-group mb-3" style={{ flex: "1 1 180px" }}>
            <label style={{ fontWeight: 600 }}>Bill Title</label>
            <input type="text" className="form-control" value={billTitle}
              onChange={e => setBillTitle(e.target.value)}
              placeholder="INVOICE / RECEIPT / BILL" />
          </div>
          <div className="form-group mb-3" style={{ flex: "1 1 140px" }}>
            <label style={{ fontWeight: 600 }}>Bill / Receipt No.</label>
            <input type="text" className="form-control" value={billNo}
              onChange={e => setBillNo(e.target.value)} placeholder="e.g. 001" />
          </div>
          <div className="form-group mb-3" style={{ flex: "1 1 160px" }}>
            <label style={{ fontWeight: 600 }}>Date</label>
            <input type="date" className="form-control" value={billDate}
              onChange={e => setBillDate(e.target.value)} />
          </div>
        </div>

        <div style={{ marginBottom: 8, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <label style={{ fontWeight: 600, margin: 0 }}>Fields</label>
          <button onClick={addField}
            style={{ padding: "5px 14px", border: "1px dashed #94a3b8", background: "transparent",
              borderRadius: 7, fontSize: 13, color: "#64748b", cursor: "pointer" }}>
            + Add Field
          </button>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 20 }}>
          {fields.map((f, i) => (
            <div key={i} style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <input type="text" placeholder="Description (e.g. Hiace)"
                value={f.name} onChange={e => updateField(i, "name", e.target.value)}
                style={{ flex: "1 1 160px", padding: "8px 12px", border: "1px solid #cbd5e1", borderRadius: 7, fontSize: 14 }} />
              <input type="text" placeholder="Amount / Value (e.g. Rs. 5000)"
                value={f.value} onChange={e => updateField(i, "value", e.target.value)}
                style={{ flex: "1 1 180px", padding: "8px 12px", border: "1px solid #cbd5e1", borderRadius: 7, fontSize: 14 }} />
              {fields.length > 1 && (
                <button onClick={() => removeField(i)}
                  style={{ background: "#fee2e2", border: "none", borderRadius: 7,
                    padding: "8px 12px", cursor: "pointer", color: "#dc2626" }}>
                  <i className="fa-solid fa-xmark" />
                </button>
              )}
            </div>
          ))}
        </div>

        <div className="form-group mb-4">
          <label style={{ fontWeight: 600 }}>Footer / Notes</label>
          <input type="text" className="form-control" value={footer}
            onChange={e => setFooter(e.target.value)}
            placeholder="e.g. Thank you for your payment" />
        </div>

        <button className="themeBtn" style={{ padding: "12px 36px", fontSize: 15 }}
          onClick={handleGenerate}>
          <i className="fa-solid fa-eye" style={{ marginRight: 8 }} />
          Generate Preview
        </button>
      </div>
    </div>
  );

  /* ── PREVIEW ── */
  return (
    <div className="DashboardMain">
      {/* Toolbar */}
      <div style={{ display: "flex", gap: 10, marginBottom: 24, alignItems: "center" }}>
        <button onClick={() => setPreview(false)}
          style={{ padding: "9px 20px", border: "1px solid #e2e8f0", borderRadius: 8,
            background: "#f9fafb", cursor: "pointer", fontSize: 14, color: "#374151" }}>
          <i className="fa-solid fa-arrow-left" style={{ marginRight: 6 }} />Edit
        </button>
        <button onClick={handlePrint}
          style={{ padding: "9px 22px", border: "none", borderRadius: 8,
            background: BRAND, color: "#fff", cursor: "pointer", fontSize: 14, fontWeight: 600 }}>
          <i className="fa-solid fa-print" style={{ marginRight: 7 }} />Print / Save PDF
        </button>
        <span style={{ fontSize: 13, color: "#9ca3af" }}>
          In the print dialog, choose "Save as PDF" to download.
        </span>
      </div>

      {/* Receipt preview */}
      <div style={{ maxWidth: 720, margin: "0 auto" }}>
        <div ref={receiptRef}>
          <div className="receipt-wrapper"
            style={{ border: `2px solid ${BRAND}`, borderRadius: 12, padding: 40, background: "#fff" }}>

            {/* Header */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center",
              borderBottom: `2px solid ${BRAND}`, paddingBottom: 20, marginBottom: 24 }}>
              <img src={logo} alt="Logo" style={{ height: 60 }} />
              <div style={{ textAlign: "right" }}>
                <h2 style={{ color: BRAND, fontSize: 22, marginBottom: 4 }}>Shabbir Transport</h2>
                {billNo && <p style={{ fontSize: 12, color: "#666", marginBottom: 2 }}>
                  Bill No: <strong>#{billNo}</strong>
                </p>}
                <p style={{ fontSize: 12, color: "#666" }}>
                  Printed: {moment().format("DD MMM YYYY, hh:mm A")}
                </p>
              </div>
            </div>

            {/* Title badge */}
            <div style={{ textAlign: "center", marginBottom: 24 }}>
              <span style={{ display: "inline-block", background: BRAND, color: "#fff",
                padding: "5px 28px", borderRadius: 20, fontSize: 14, fontWeight: 700,
                letterSpacing: 3, textTransform: "uppercase" }}>
                {billTitle || "INVOICE"}
              </span>
            </div>

            {/* Issued To + Date */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px 24px", marginBottom: 24 }}>
              <div style={{ background: BRAND_LIGHT, border: `1px solid ${BRAND_BORDER}`, borderRadius: 8, padding: "14px 20px" }}>
                <label style={{ fontSize: 11, color: "#888", textTransform: "uppercase", letterSpacing: 1, display: "block", marginBottom: 4 }}>
                  Issued To
                </label>
                <span style={{ fontSize: 16, fontWeight: 700, color: BRAND }}>{orgName}</span>
              </div>
              <div style={{ background: BRAND_LIGHT, border: `1px solid ${BRAND_BORDER}`, borderRadius: 8, padding: "14px 20px" }}>
                <label style={{ fontSize: 11, color: "#888", textTransform: "uppercase", letterSpacing: 1, display: "block", marginBottom: 4 }}>
                  Date
                </label>
                <span style={{ fontSize: 16, fontWeight: 700, color: BRAND }}>
                  {moment(billDate).format("DD MMM YYYY")}
                </span>
              </div>
            </div>

            {/* Section label */}
            <div style={{ fontSize: 13, fontWeight: 700, textTransform: "uppercase",
              letterSpacing: 1, color: BRAND, marginBottom: 10 }}>
              Details
            </div>

            {/* Fields table */}
            <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 24 }}>
              <thead>
                <tr>
                  <th style={{ background: BRAND, color: "#fff", padding: "10px 16px", textAlign: "left", fontSize: 13 }}>
                    Description
                  </th>
                  <th style={{ background: BRAND, color: "#fff", padding: "10px 16px", textAlign: "right", fontSize: 13 }}>
                    Amount / Details
                  </th>
                </tr>
              </thead>
              <tbody>
                {filledFields.length > 0 ? filledFields.map((f, i) => (
                  <tr key={i}>
                    <td style={{ padding: "11px 16px", borderBottom: "1px solid #e5e7eb", fontSize: 14,
                      background: i % 2 === 1 ? "#f5f8fc" : "#fff" }}>
                      {f.name}
                    </td>
                    <td style={{ padding: "11px 16px", borderBottom: "1px solid #e5e7eb", fontSize: 14,
                      textAlign: "right", fontWeight: 600, background: i % 2 === 1 ? "#f5f8fc" : "#fff" }}>
                      {f.value || "—"}
                    </td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan={2} style={{ padding: "16px", textAlign: "center", color: "#9ca3af", fontSize: 13 }}>
                      No items
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            {/* Footer note */}
            {footer && (
              <div style={{ background: BRAND_LIGHT, border: `1px solid ${BRAND_BORDER}`, borderRadius: 8,
                padding: "12px 16px", marginBottom: 24, textAlign: "center",
                fontSize: 13, color: BRAND, fontStyle: "italic" }}>
                {footer}
              </div>
            )}

            {/* Bottom stamp */}
            <div style={{ textAlign: "center", fontSize: 11, color: "#aaa",
              borderTop: "1px solid #e5e7eb", paddingTop: 16, marginTop: 8 }}>
              <p>This is a computer-generated document. No signature required.</p>
              <p style={{ marginTop: 4 }}>Shabbir Transport &mdash; {moment().format("DD MMM YYYY, hh:mm A")}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
